import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Key, Shield, Zap, Server, Plus, Trash2, Copy, Eye, EyeOff,
  BarChart3, ArrowRight, CheckCircle, AlertTriangle, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

import { ProviderIcon } from "@/components/logos";

const PROVIDER_CONFIG = {
  openai: { name: "OpenAI", color: "#00A67E", models: ["gpt-5.2", "gpt-4o", "gpt-4o-mini"] },
  anthropic: { name: "Anthropic", color: "#E87B35", models: ["claude-sonnet-4", "claude-haiku-4"] },
  google: { name: "Google", color: "#06B6D4", models: ["gemini-3-flash", "gemini-3-pro"] },
  moonshot: { name: "Moonshot AI", color: "#8B5CF6", models: ["kimi-k2.5", "kimi-k2.5-chat"] },
  "self-hosted": { name: "Self-Hosted", color: "#F59E0B", models: ["gemma-4-27b-it", "gemma-4-e4b-it", "gemma-4-e2b-it", "gemma-4-31b-it"] },
  local: { name: "Local/Self-hosted", color: "#9CA3AF", models: ["local"] },
};

export default function ModelProvisioningHub() {
  const [provisioning, setProvisioning] = useState(null);
  const [models, setModels] = useState([]);
  const [virtualKeys, setVirtualKeys] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [credits, setCredits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [setupOpen, setSetupOpen] = useState(false);
  const [keyDialogOpen, setKeyDialogOpen] = useState(false);
  const [newKeyName, setNewKeyName] = useState("Default Key");
  const [newKey, setNewKey] = useState(null);
  const [showApiKey, setShowApiKey] = useState({});

  // Setup form state
  const [setupMode, setSetupMode] = useState("byok");
  const [setupProviders, setSetupProviders] = useState({});
  const [setupDefaultModel, setSetupDefaultModel] = useState("gpt-4o");

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [provRes, modelsRes, keysRes, analyticsRes, creditsRes] = await Promise.all([
        axios.get(`${API}/provisioning/status`).catch(() => ({ data: {} })),
        axios.get(`${API}/gateway/models`).catch(() => ({ data: { models: [] } })),
        axios.get(`${API}/gateway/keys`).catch(() => ({ data: [] })),
        axios.get(`${API}/gateway/analytics`).catch(() => ({ data: {} })),
        axios.get(`${API}/credits/balance`).catch(() => ({ data: { balance: 0 } })),
      ]);
      setProvisioning(provRes.data);
      setModels(modelsRes.data.models || []);
      setVirtualKeys(keysRes.data);
      setAnalytics(analyticsRes.data);
      setCredits(creditsRes.data);
    } catch (e) {
      console.error("Failed to load provisioning data:", e);
    }
    setLoading(false);
  };

  const handleSetup = async () => {
    try {
      await axios.post(`${API}/provisioning/setup`, {
        mode: setupMode,
        providers: setupProviders,
        default_model: setupDefaultModel,
      });
      toast.success("Provisioning configured!");
      setSetupOpen(false);
      loadAll();
    } catch (e) {
      toast.error("Failed to configure provisioning");
    }
  };

  const handleCreateKey = async () => {
    try {
      const res = await axios.post(`${API}/gateway/keys?name=${encodeURIComponent(newKeyName)}`);
      setNewKey(res.data);
      toast.success("Virtual key created!");
      loadAll();
    } catch (e) {
      toast.error("Failed to create key");
    }
  };

  const handleRevokeKey = async (keyId) => {
    try {
      await axios.delete(`${API}/gateway/keys/${keyId}`);
      toast.success("Key revoked");
      loadAll();
    } catch (e) {
      toast.error("Failed to revoke key");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <RefreshCw className="w-6 h-6 animate-spin text-[var(--qn-primary)]" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Gateway Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]" data-testid="gateway-requests-card" data-automation-id="gateway-requests-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[var(--qn-primary)]/10">
                <Zap className="w-5 h-5 text-[var(--qn-primary)]" />
              </div>
              <div>
                <div className="text-sm text-[var(--qn-foreground-muted)]">Total Requests</div>
                <div className="text-2xl font-semibold">{analytics?.total_requests || 0}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]" data-testid="gateway-tokens-card" data-automation-id="gateway-tokens-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[var(--qn-secondary)]/10">
                <BarChart3 className="w-5 h-5 text-[var(--qn-secondary)]" />
              </div>
              <div>
                <div className="text-sm text-[var(--qn-foreground-muted)]">Total Tokens</div>
                <div className="text-2xl font-semibold">{(analytics?.total_tokens || 0).toLocaleString()}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]" data-testid="gateway-savings-card" data-automation-id="gateway-savings-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[var(--qn-success)]/10">
                <CheckCircle className="w-5 h-5 text-[var(--qn-success)]" />
              </div>
              <div>
                <div className="text-sm text-[var(--qn-foreground-muted)]">Gateway Savings</div>
                <div className="text-2xl font-semibold text-[var(--qn-success)]">${(analytics?.total_savings || 0).toFixed(4)}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]" data-testid="gateway-credits-card" data-automation-id="gateway-credits-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10">
                <Shield className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <div className="text-sm text-[var(--qn-foreground-muted)]">Credit Balance</div>
                <div className="text-2xl font-semibold">${(credits?.balance || 0).toFixed(2)}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Provisioning Status & Setup */}
      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Server className="w-5 h-5 text-[var(--qn-primary)]" />
              Provisioning
            </CardTitle>
            <CardDescription className="text-[var(--qn-foreground-muted)]">
              Configure how your LLM requests are routed
            </CardDescription>
          </div>
          <Button onClick={() => setSetupOpen(true)} className="btn-primary" data-testid="setup-provisioning-btn" data-automation-id="setup-provisioning-btn">
            {provisioning?.provisioned ? "Update Config" : "Set Up Gateway"}
          </Button>
        </CardHeader>
        <CardContent>
          {provisioning?.provisioned ? (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <Badge className={`${provisioning.mode === 'byok' ? 'bg-blue-500/10 text-blue-400' : 'bg-purple-500/10 text-purple-400'}`}>
                  {provisioning.mode === 'byok' ? 'Bring Your Own Key' : provisioning.mode === 'managed' ? 'QNNEAL Managed' : 'Hybrid'}
                </Badge>
                <Badge className="bg-[var(--qn-success)]/10 text-[var(--qn-success)]">
                  {provisioning.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </div>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div className="bg-[var(--qn-background)] rounded-lg p-3">
                  <div className="text-[var(--qn-foreground-subtle)]">Default Model</div>
                  <div className="font-medium mt-1">{provisioning.default_model}</div>
                </div>
                <div className="bg-[var(--qn-background)] rounded-lg p-3">
                  <div className="text-[var(--qn-foreground-subtle)]">Providers</div>
                  <div className="font-medium mt-1">{Object.keys(provisioning.providers || {}).length} configured</div>
                </div>
                <div className="bg-[var(--qn-background)] rounded-lg p-3">
                  <div className="text-[var(--qn-foreground-subtle)]">Gateway Endpoint</div>
                  <div className="font-mono text-xs mt-1">/v1/chat/completions</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-[var(--qn-foreground-muted)]">
              <Server className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p>No provisioning configured. Set up your LLM gateway to start routing requests.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Available Models */}
      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-[var(--qn-primary)]" />
            Available Models
          </CardTitle>
          <CardDescription className="text-[var(--qn-foreground-muted)]">
            Models available through the QNNEAL gateway with pricing
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {models.map((model) => {
              const provider = PROVIDER_CONFIG[model.provider] || {};
              return (
                <div
                  key={model.id}
                  className="bg-[var(--qn-background)] rounded-lg p-4 border border-[var(--qn-border)] hover:border-[var(--qn-primary)]/30 transition-colors"
                  data-testid={`model-card-${model.id}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <ProviderIcon providerKey={model.provider} size="sm" variant="color" />
                    <span className="font-medium text-sm">{model.id}</span>
                  </div>
                  <div className="text-xs text-[var(--qn-foreground-subtle)] mb-2">{provider.name || model.provider}</div>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-[var(--qn-foreground-subtle)]">Input/1K:</span>
                      <span>${model.pricing.input_per_1k}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[var(--qn-foreground-subtle)]">Output/1K:</span>
                      <span>${model.pricing.output_per_1k}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {model.complexity_levels?.map((level) => (
                      <Badge key={level} variant="outline" className="text-xs border-[var(--qn-border)]">{level}</Badge>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Virtual Keys */}
      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-amber-500" />
              Virtual API Keys
            </CardTitle>
            <CardDescription className="text-[var(--qn-foreground-muted)]">
              Keys for authenticating with the QNNEAL gateway
            </CardDescription>
          </div>
          <Button onClick={() => { setNewKey(null); setKeyDialogOpen(true); }} className="btn-primary" data-testid="create-vkey-btn" data-automation-id="create-vkey-btn">
            <Plus className="w-4 h-4 mr-2" />
            Create Key
          </Button>
        </CardHeader>
        <CardContent>
          {virtualKeys.length > 0 ? (
            <div className="space-y-3">
              {virtualKeys.map((vk) => (
                <div
                  key={vk.key_id}
                  className="flex items-center justify-between bg-[var(--qn-background)] rounded-lg p-3 border border-[var(--qn-border)]"
                  data-testid={`vkey-${vk.key_id}`}
                >
                  <div className="flex items-center gap-3">
                    <Key className="w-4 h-4 text-[var(--qn-foreground-subtle)]" />
                    <div>
                      <div className="font-medium text-sm">{vk.name}</div>
                      <div className="text-xs text-[var(--qn-foreground-subtle)] font-mono">{vk.key_prefix}...</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={vk.status === 'active' ? 'bg-[var(--qn-success)]/10 text-[var(--qn-success)]' : 'bg-red-500/10 text-red-400'}>
                      {vk.status}
                    </Badge>
                    <span className="text-xs text-[var(--qn-foreground-subtle)]">{vk.total_requests || 0} reqs</span>
                    <Button
                      variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:bg-red-500/10"
                      onClick={() => handleRevokeKey(vk.key_id)}
                      data-testid={`revoke-vkey-${vk.key_id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-[var(--qn-foreground-muted)]">
              <Key className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No virtual keys yet. Create one to use the gateway.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Model Distribution (if analytics available) */}
      {analytics?.model_distribution && Object.keys(analytics.model_distribution).length > 0 && (
        <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-[var(--qn-secondary)]" />
              Model Usage Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(analytics.model_distribution).map(([model, count]) => {
                const total = analytics.total_requests || 1;
                const pct = Math.round((count / total) * 100);
                return (
                  <div key={model} className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span>{model}</span>
                      <span className="text-[var(--qn-foreground-subtle)]">{count} ({pct}%)</span>
                    </div>
                    <Progress value={pct} className="h-2" />
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Setup Dialog */}
      <Dialog open={setupOpen} onOpenChange={setSetupOpen}>
        <DialogContent className="bg-[var(--qn-card)] border-[var(--qn-border)] max-w-lg">
          <DialogHeader>
            <DialogTitle>Configure LLM Gateway</DialogTitle>
            <DialogDescription className="text-[var(--qn-foreground-muted)]">
              Choose how you want to provision LLM access
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Provisioning Mode</label>
              <Select value={setupMode} onValueChange={setSetupMode}>
                <SelectTrigger className="bg-[var(--qn-background)] border-[var(--qn-border)]" data-testid="setup-mode-select" data-automation-id="setup-mode-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[var(--qn-card)] border-[var(--qn-border)]">
                  <SelectItem value="byok">Bring Your Own Key (BYOK)</SelectItem>
                  <SelectItem value="managed">QNNEAL Managed</SelectItem>
                  <SelectItem value="hybrid">Hybrid</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Default Model</label>
              <Select value={setupDefaultModel} onValueChange={setSetupDefaultModel}>
                <SelectTrigger className="bg-[var(--qn-background)] border-[var(--qn-border)]" data-testid="setup-model-select" data-automation-id="setup-model-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[var(--qn-card)] border-[var(--qn-border)]">
                  {Object.entries(PROVIDER_CONFIG).flatMap(([, config]) =>
                    config.models.map((m) => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            {setupMode !== "managed" && (
              <div className="bg-[var(--qn-background)] rounded-lg p-3 text-sm text-[var(--qn-foreground-muted)]">
                <AlertTriangle className="w-4 h-4 inline mr-2 text-amber-500" />
                In BYOK mode, you'll need to provide your own API keys for each LLM provider. Keys are encrypted and stored securely.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSetupOpen(false)}>Cancel</Button>
            <Button onClick={handleSetup} className="btn-primary" data-testid="confirm-setup-btn" data-automation-id="confirm-setup-btn">Save Configuration</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Key Dialog */}
      <Dialog open={keyDialogOpen} onOpenChange={setKeyDialogOpen}>
        <DialogContent className="bg-[var(--qn-card)] border-[var(--qn-border)]">
          <DialogHeader>
            <DialogTitle>Create Virtual API Key</DialogTitle>
            <DialogDescription className="text-[var(--qn-foreground-muted)]">
              Generate a new key for gateway authentication
            </DialogDescription>
          </DialogHeader>
          {newKey ? (
            <div className="space-y-4">
              <div className="bg-[var(--qn-success)]/10 text-[var(--qn-success)] rounded-lg p-3 text-sm flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Key created successfully! Copy it now - it will not be shown again.
              </div>
              <div className="flex items-center gap-2">
                <Input
                  value={newKey.key}
                  readOnly
                  className="bg-[var(--qn-background)] border-[var(--qn-border)] font-mono text-sm"
                  data-testid="new-vkey-value" data-automation-id="new-vkey-value"
                />
                <Button onClick={() => copyToClipboard(newKey.key)} size="icon" className="btn-primary shrink-0">
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <Input
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                placeholder="Key name"
                className="bg-[var(--qn-background)] border-[var(--qn-border)]"
                data-testid="new-vkey-name" data-automation-id="new-vkey-name"
              />
            </div>
          )}
          <DialogFooter>
            {newKey ? (
              <Button onClick={() => setKeyDialogOpen(false)} className="btn-primary">Done</Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => setKeyDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleCreateKey} className="btn-primary" data-testid="confirm-create-vkey-btn" data-automation-id="confirm-create-vkey-btn">Generate Key</Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
